// @flow
import { get, filter } from 'lodash';
import moment from 'moment';
import { rndId, isPresent, reportError } from '@helpers/helpers';
import { countryLanguages } from '@helpers/constants';
import { Api } from '../../../helpers';
import { unanswered } from '../../../helpers/Api';
import ClinicService from '../../../services/ClinicService';
import { router } from '../../../routes/router';
import { logout as userLogout } from '../auth';
import type
{
  Action,
  ContextState,
  User,
  UserInput,
  Dispatch,
  WorkHours,
  RichTextEditorFlags,
} from '../../../types';

type UserResponseType = {
  user: User,
};


// ------------------------------------
// Constants
// ------------------------------------

const USER_LOADING = 'user.USER_LOADING';
const USER_SET_LOADING = 'user.USER_SET_LOADING';

const USER_UPDATING = 'user.USER_UPDATING';
const USER_UPDATE_SUCCESS = 'user.USER_UPDATE_SUCCESS';
const USER_UPDATE_ERROR = 'user.USER_UPDATE_ERROR';

const USER_RICH_TEXT_EDITOR_SAVING = 'user.USER_RICH_TEXT_EDITOR_SAVING';
const USER_RICH_TEXT_EDITOR_SAVED = 'user.USER_RICH_TEXT_EDITOR_SAVED';

const USER_DELETING = 'user.USER_DELETING';
const USER_DELETE_SUCCESS = 'user.USER_DELETE_SUCCESS';
const USER_DELETE_ERROR = 'user.USER_DELETE_ERROR';

const USER_LOADED = 'user.USER_LOADED';
const USER_LOADING_FAILED = 'user.USER_LOADING_FAILED';

const USER_UPDATE_DOCTOR_PRIVACY_LOADING = 'users.USER_UPDATE_DOCTOR_PRIVACY_LOADING';
const USER_UPDATE_DOCTOR_PRIVACY_LOADED = 'users.USER_UPDATE_DOCTOR_PRIVACY_LOADED';
const USER_UPDATE_DOCTOR_PRIVACY_FAILED = 'users.USER_UPDATE_DOCTOR_PRIVACY_FAILED';

const USER_CREATE_DOCTOR_WORKHOURS_LOADING = 'users.USER_CREATE_DOCTOR_WORKHOURS_LOADING';
const USER_CREATE_DOCTOR_WORKHOURS_LOADED = 'users.USER_CREATE_DOCTOR_WORKHOURS_LOADED';
const USER_CREATE_DOCTOR_WORKHOURS_FAILED = 'users.USER_CREATE_DOCTOR_WORKHOURS_FAILED';

const USER_DELETE_DOCTOR_WORKHOURS_LOADING = 'users.USER_DELETE_DOCTOR_WORKHOURS_LOADING';
const USER_DELETE_DOCTOR_WORKHOURS_LOADED = 'users.USER_DELETE_DOCTOR_WORKHOURS_LOADED';
const USER_DELETE_DOCTOR_WORKHOURS_FAILED = 'users.USER_DELETE_DOCTOR_WORKHOURS_FAILED';

const USER_RECOVER_PASSWORD_LOADING = 'users.USER_RECOVER_PASSWORD_LOADING';
const USER_RECOVER_PASSWORD_LOADED = 'users.USER_RECOVER_PASSWORD_LOADED';
const USER_RECOVER_PASSWORD_FAILED = 'users.USER_RECOVER_PASSWORD_FAILED';

const USER_RECOVER_PASSWORD_RESET = 'users.USER_RECOVER_PASSWORD_RESET';

const USER_REQUEST_BACKUP_LOADING = 'users.USER_REQUEST_BACKUP_LOADING';
const USER_REQUEST_BACKUP_LOADED = 'users.USER_REQUEST_BACKUP_LOADED';
const USER_REQUEST_BACKUP_FAILED = 'users.USER_REQUEST_BACKUP_FAILED';
const USER_REQUEST_BACKUP_RESET = 'users.USER_REQUEST_BACKUP_RESET';

const USER_CREATE_CLINIC_LOADING = 'users.USER_CREATE_CLINIC_LOADING';
const USER_CREATE_CLINIC_LOADED = 'users.USER_CREATE_CLINIC_LOADED';
const USER_CREATE_CLINIC_FAILED = 'users.USER_CREATE_CLINIC_FAILED';

const USER_UPDATE_PASSWORD_LOADING = 'users.USER_UPDATE_PASSWORD_LOADING';
const USER_UPDATE_PASSWORD_LOADED = 'users.USER_UPDATE_PASSWORD_LOADED';
const USER_UPDATE_PASSWORD_FAILED = 'users.USER_UPDATE_PASSWORD_FAILED';
const USER_UPDATE_PASSWORD_RESET = 'users.USER_UPDATE_PASSWORD_RESET';

const USER_INITIALIZE_WORKHOURS_LOAD_STATE = 'users.USER_INITIALIZE_WORKHOURS_LOAD_STATE';


export const logout = (reload: boolean = false) => (
  (dispatch: Dispatch) => {
    dispatch(userLogout(reload));
  }
);

export const resetRecoverState = () => (
  (dispatch: Dispatch) => {
    dispatch({
      type: USER_RECOVER_PASSWORD_RESET,
    });
  }
);
export const resetRequestBackup = () => (
  (dispatch: Dispatch) => {
    dispatch({
      type: USER_REQUEST_BACKUP_RESET,
    });
  }
);

export const updateUserPasswordReset = () => (
  (dispatch: Dispatch) => {
    dispatch({
      type: USER_UPDATE_PASSWORD_RESET,
    });
  }
);

export const createClinic = () => (
  (dispatch: Dispatch) => {
    dispatch({ type: USER_CREATE_CLINIC_LOADING });
    Api.post(router.getApiRoute('createClinic', { }), {
      success: (response) => {
        dispatch({
          type: USER_CREATE_CLINIC_LOADED,
          activeClinic: get(response.data, 'clinic'),
          user: get(response.data, 'user'),
        });
        dispatch(router.pushPath('patientsList'));
      },
      error: (error) => {
        dispatch({
          type: USER_CREATE_CLINIC_FAILED, error: { message: error.message },
        });
        dispatch(reportError(error));
      },
    });
  }
);
export const requestBackup = (backupType: 'patients' | 'full', cb?: Function = (() => {})) => (
  (dispatch: Dispatch) => {
    dispatch({ type: USER_REQUEST_BACKUP_LOADING });
    Api.post(router.getApiRoute('requestBackup', { }), {
      data: {
        backup_type: backupType,
      },
      success: () => {
        dispatch({
          type: USER_REQUEST_BACKUP_LOADED,
        });
        if (cb) {
          cb(true);
        }
      },
      error: (error) => {
        dispatch({
          type: USER_REQUEST_BACKUP_FAILED, error: { message: error.message },
        });
        if (cb) {
          cb(false);
        }
        dispatch(reportError(error));
      },
    });
  }
);

// The API answers a wrong current password with a 400 and nothing in it (head :bad_request): the user's mistake, which
// the screen shows under the field, so no report and no toast. Its other 400s on this route say something, like
// wrong_clinic's message for the user. Rails' own 400 for a body it can't read is empty too, but this one is always
// JSON.stringify'd from two strings.
const wrongCurrentPassword = (status: any, data: any): boolean => status === 400 && !data;

export const updateUserPassword = (password: string, oldPassword: string) => (
  (dispatch: Dispatch) => {
    dispatch({ type: USER_UPDATE_PASSWORD_LOADING });
    Api.patch(router.getApiRoute('updateUserPassword', { }), {
      data: {
        user: {
          password,
          oldPassword,
        },
      },
      handledStatuses: wrongCurrentPassword,
      success: () => {
        dispatch({
          type: USER_UPDATE_PASSWORD_LOADED,
        });
      },
      error: (error) => {
        const wrongPassword = wrongCurrentPassword(error.status, error.data);
        dispatch({
          type: USER_UPDATE_PASSWORD_FAILED, wrongPassword, error: { message: error.message },
        });
        if (!wrongPassword) dispatch(reportError(error));
      },
    });
  }
);

export const recoverPassword = (email: string) => (
  (dispatch: Dispatch) => {
    dispatch({ type: USER_RECOVER_PASSWORD_LOADING });
    Api.post(router.getApiRoute('recoverPassword', { }), {
      data: {
        reset: {
          email,
        },
      },
      success: () => {
        dispatch({
          type: USER_RECOVER_PASSWORD_LOADED,
        });
      },
      error: (error) => {
        dispatch({
          type: USER_RECOVER_PASSWORD_FAILED, error: { message: error.message },
        });
        dispatch(reportError(error));
      },
    });
  }
);


export const createDoctorWorkHours = (clinicId: number, doctorId: number, workHours: WorkHours) => (
  (dispatch: Dispatch) => {
    const futureId = rndId();
    dispatch({ type: USER_CREATE_DOCTOR_WORKHOURS_LOADING, doctorId, future: { id: futureId, ...workHours, isFuture: true } });
    Api.post(router.getApiRoute('createDoctorWorkHours', { doctorId }), {
      query: { clinicId },
      data: {
        work_hour: {
          startTime: workHours.start,
          endTime: workHours.end,
          weekday: workHours.weekday,
        },
      },
      success: (response) => {
        const respData = get(response, ['data', 'work_hour']);
        dispatch({
          type: USER_CREATE_DOCTOR_WORKHOURS_LOADED,
          workHours: {
            id: respData.id,
            start: respData.startTime,
            end: respData.endTime,
            weekday: respData.weekday,
          },
          futureId,
          doctorId,
        });
      },
      error: (error) => {
        dispatch({
          type: USER_CREATE_DOCTOR_WORKHOURS_FAILED, doctorId, futureId, error: { message: error.message },
        });
        dispatch(reportError(error));
      },
    });
  }
);


export const deleteDoctorWorkHours = (clinicId: number, doctorId: number, id: number) => (
  (dispatch: Dispatch) => {
    dispatch({ type: USER_DELETE_DOCTOR_WORKHOURS_LOADING, doctorId, deleteId: id });
    Api.destroy(router.getApiRoute('deleteDoctorWorkHours', { id, doctorId }), {
      query: { clinicId },
      success: () => {
        dispatch({
          type: USER_DELETE_DOCTOR_WORKHOURS_LOADED,
          deleteId: id,
          doctorId,
        });
      },
      error: (error) => {
        dispatch({
          type: USER_DELETE_DOCTOR_WORKHOURS_FAILED, doctorId, deleteId: id, error: { message: error.message },
        });
        dispatch(reportError(error));
      },
    });
  }
);

export const updateDoctorPrivacy = (clinicId: number, doctorId: number, defaultVisibilityLevel: number) => (
  (dispatch: Dispatch) => {
    if (!isPresent(defaultVisibilityLevel)) {
      dispatch({ type: USER_UPDATE_DOCTOR_PRIVACY_FAILED, error: { message: 'Invalid option' } });
      return;
    }
    dispatch({ type: USER_UPDATE_DOCTOR_PRIVACY_LOADING });
    Api.patch(router.getApiRoute('updateDoctorPrivacy', { id: doctorId }), {
      query: { clinicId },
      data: {
        doctor: {
          defaultVisibilityLevel,
        },
      },
      success: () => {
        dispatch({
          type: USER_UPDATE_DOCTOR_PRIVACY_LOADED,
          doctorDefaultVisibilityLevel: defaultVisibilityLevel,
        });
      },
      error: (error) => {
        dispatch({ type: USER_UPDATE_DOCTOR_PRIVACY_FAILED, error: { message: error.message } });
        dispatch(reportError(error));
      },
    });
  }
);

// The order of the user's reads and the editor switch's answers, this session: a read sent before the switch's latest
// answer came may carry the flags from before it, so the reducer keeps the switch's (withSwitchKept)
let userClock = 0;
const nextUserClock = (): number => {
  userClock += 1;
  return userClock;
};

export const getUser = () => ({
  types: [USER_LOADING, USER_LOADED, USER_LOADING_FAILED],
  payload: { requestedAt: nextUserClock() },

  shouldCall: (state: ContextState) => {
    const authenticated = isPresent(get(state, ['auth', 'token']));

    return Boolean(authenticated);
  },
  // options: Api.get's (the app's start passes a signal and a timeout)
  call: (state?: ContextState, options?: Object) => Api.get(router.getApiRoute('getUser', null), options),
  parseResponse: (response: UserResponseType) => {
    let countryCode = get(response, ['user', 'countryCode']);
    countryCode = countryCode || window.countryCode;
    countryCode = countryCode || 'BR';

    if (localStorage.getItem('debugLng') !== 'true') {
      window.i18n.changeLanguage(get(countryLanguages, countryCode, 'pt-BR'));
    }
    return {
      user: response.user,
    };
  },
});

export const updateUser = (user: UserInput) => (dispatch: Dispatch) => {
  dispatch({ type: USER_UPDATING });
  const requestedAt = nextUserClock();
  Api.patch(router.getApiRoute('updateUser', null), {
    data: { user },
    success: (response) => {
      dispatch({
        type: USER_UPDATE_SUCCESS,
        user: get(response, 'user'),
        requestedAt,
      });
    },
    error: (error) => {
      dispatch({ type: USER_UPDATE_ERROR, error: { message: error.message } });
      dispatch(reportError(error));
    },
  });
};

// The user's own switch for the record's rich-text editor (Ajustes, only in its rollout): the record field follows it
// at once, and it is saved in the background (users.rich_text_editor_disabled). The store keeps the switches still out
// (context.richTextEditorSave), so Ajustes takes no other tap while one is, a menu opened again too. An answer counts
// only for the user who switched, in the same session (signed out, or another user signed in meanwhile: it changes
// nothing). While the latest switch is out the field shows it; once it is back, what the server last confirmed (its
// answer to the latest switch it took): a failed latest switch goes back to that and says so, an older one's failure
// says nothing. A switch left unanswered fails after RICH_TEXT_EDITOR_SAVE_TIMEOUT, as any other failure (so Ajustes
// never stays locked); the server may have taken it all the same, so once none is out the user is read again. Resolves
// whether it was saved.
export const RICH_TEXT_EDITOR_SAVE_TIMEOUT = 15000;
// A switch's answer still counts, for the thunk and the reducer alike (`context`: the store's context): the same user
// signed in, and the store still waiting for that request (a sign-out clears it)
const richTextEditorAnswerCounts = (context: ?ContextState, userId: number, request: number): boolean => {
  const save = get(context, ['richTextEditorSave']);
  return get(context, ['user', 'id']) === userId && Boolean(save) && save.userId === userId
    && save.pending.includes(request);
};
let richTextEditorRequest = 0;
export const setRichTextEditorEnabled = (enabled: boolean) => (dispatch: Dispatch, getState: Function): Promise<boolean> => {
  const userId = get(getState(), ['context', 'user', 'id']);
  const flags: ?RichTextEditorFlags = get(getState(), ['context', 'user', 'advancedData', 'richTextEditor']);
  if (!userId || !flags || flags.access !== true) return Promise.resolve(false);
  richTextEditorRequest += 1;
  const request = richTextEditorRequest;
  dispatch({
    type: USER_RICH_TEXT_EDITOR_SAVING, userId, request, enabled,
  });
  const counts = () => richTextEditorAnswerCounts(get(getState(), ['context']), userId, request);
  // A switch sent and never answered (a timeout, a dropped connection) may be on the server: read the user once none
  // is out, so the field ends as the server has it
  const answer = (action: Object) => {
    const resync = Boolean(action.unanswered || get(getState(), ['context', 'richTextEditorSave', 'resync']));
    dispatch({ ...action, answeredAt: nextUserClock() });
    if (resync && !get(getState(), ['context', 'richTextEditorSave'])) dispatch(getUser());
  };
  return new Promise((resolve) => {
    Api.patch(router.getApiRoute('updateUser', null), {
      data: { user: { richTextEditorDisabled: !enabled } },
      timeout: RICH_TEXT_EDITOR_SAVE_TIMEOUT,
      success: (response) => {
        if (!counts()) {
          resolve(false);
          return;
        }
        const saved = get(response, ['user', 'advancedData', 'richTextEditor']) || { enabled };
        answer({
          type: USER_RICH_TEXT_EDITOR_SAVED, userId, request, saved,
        });
        resolve(true);
      },
      error: (error) => {
        if (!counts()) {
          resolve(false);
          return;
        }
        const latest = get(getState(), ['context', 'richTextEditorSave', 'latest']) === request;
        answer({
          type: USER_RICH_TEXT_EDITOR_SAVED, userId, request, failed: true, unanswered: unanswered(error),
        });
        if (latest) dispatch(reportError(error));
        resolve(false);
      },
    });
  });
};

export const deleteMyAccount = (callback?: Function) => (dispatch: Dispatch) => {
  dispatch({ type: USER_DELETING });
  Api.destroy(router.getApiRoute('deleteMyAccount', null), {
    success: (response) => {
      dispatch({
        type: USER_DELETE_SUCCESS,
        user: get(response, 'user'),
      });

      if (callback) callback();
    },
    error: (error) => {
      dispatch({ type: USER_DELETE_ERROR, error: { message: error.message } });
      dispatch(reportError(error));
    },
  });
};

export const setUserLoading = (isLoading: boolean) => (dispatch: Dispatch) => {
  dispatch({ type: USER_SET_LOADING, isLoading });
};

export const resetWorkhoursFormState = () => (dispatch:Dispatch) => {
  dispatch({ type: USER_INITIALIZE_WORKHOURS_LOAD_STATE });
};

// The API's own web pages (the Painel de Controle's /dashboard, /app/...), which open signed in through
// dashboardAccessUrl, on the host of the API this build talks to: window.API, the build's API_SERVER. Production's
// API (api.doutore.com) serves them at beta.doutore.com; any other API at its own origin (staging-api.doutore.com,
// a local API, or the Mac's LAN IP that a phone reaches it by). http://localhost:3000 when API_SERVER is not an
// http(s) URL. Not NODE_ENV: `webpack --mode production` makes it "production" in every CI bundle, staging's included.
const PRODUCTION_API_HOST = 'api.doutore.com';
const apiUrl = (): ?URL => {
  try {
    const url = new URL(String(window.API || ''));
    // 'localhost:3000' parses too, as a "localhost:" scheme
    return /^https?:$/.test(url.protocol) ? url : null;
  } catch (e) {
    return null;
  }
};
export const dashboardBaseUrl = (): string => {
  const url = apiUrl();
  if (!url) return 'http://localhost:3000';
  // The host alone: a port (:443), case (URL lowercases an http(s) host) or a trailing dot ("api.doutore.com.") is
  // still production's API
  const host = url.hostname.replace(/\.$/, '');
  return host === PRODUCTION_API_HOST ? 'https://beta.doutore.com' : url.origin;
};

// A build in App Store review asks the dashboard to keep billing out of that browser (guideline 3.1.1)
export const dashboardAccessUrl = (dashboardUrl: string, token: string, redirectTo: string) => (
  `${dashboardUrl}/access?access_token=${token}${Api.inStoreReview() ? '&store_review=1' : ''}&redirect_to=${redirectTo}`
);

// A page of the API's web app, signed in with the token, on dashboardBaseUrl's host. `path` is the page's whole path
// there: /dashboard/... for the Painel de Controle, /app/... for the pages the app opens (patient link, Memed).
// The URL carries the user's token: it is for the user's own browser.
export const dashboardPageUrl = (token: string, path: string): string => {
  const baseUrl = dashboardBaseUrl();
  return dashboardAccessUrl(`${baseUrl}/dashboard`, token, `${baseUrl}${path}`);
};

export const redirectToDashboard = (to: string, token: string) => {
  const dashboardUrl = router.getApiRoute('dashboard', null).path;
  const url = dashboardAccessUrl(dashboardUrl, token, `${dashboardUrl}${to}`);

  window.open(url, '_blank');
};

export const directUpdateUser = (user: any) => ({ type: USER_LOADED, user });

export const userActions = {
  getUser,
  updateUser,
  setRichTextEditorEnabled,
  deleteMyAccount,
  setUserLoading,
  updateDoctorPrivacy,
  createDoctorWorkHours,
  deleteDoctorWorkHours,
  resetWorkhoursFormState,
  logout,
  createClinic,
  directUpdateUser,
  redirectToDashboard,
};

export const actionTypes = {
  USER_LOADING,
  USER_LOADED,
  USER_LOADING_FAILED,
  USER_UPDATING,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_ERROR,
  USER_RICH_TEXT_EDITOR_SAVING,
  USER_RICH_TEXT_EDITOR_SAVED,
  USER_UPDATE_DOCTOR_PRIVACY_LOADING,
  USER_UPDATE_DOCTOR_PRIVACY_LOADED,
  USER_UPDATE_DOCTOR_PRIVACY_FAILED,
  USER_INITIALIZE_WORKHOURS_LOAD_STATE,
  USER_SET_LOADING,
};

// The user's rich-text editor flags with `flags` in them: only for a user that is there (signed out meanwhile: nothing),
// and only the flags given
const withRichTextEditorFlags = (state: ContextState, flags: Object): ContextState => {
  const current = get(state, ['user']);
  if (!current || !current.id) return state;
  const advancedData = current.advancedData || {};
  return state.set('user', {
    ...current,
    advancedData: { ...advancedData, richTextEditor: { ...advancedData.richTextEditor, ...flags } },
  });
};

// The signed-in user as a read or a save of it brings it (`requestedAt`: when it was sent, nextUserClock), with the
// editor's flags the field shows kept while a switch is out, and when it was sent before the switch's latest answer
// came: the server may have read the user before it took the switch
const withSwitchKept = (state: ContextState, user: User, requestedAt: ?number): User => {
  const current = get(state, ['user']);
  const flags = get(current, ['advancedData', 'richTextEditor']);
  if (!user || !current || user.id !== current.id || !flags) return user;
  const save = get(state, ['richTextEditorSave']);
  const out = Boolean(save) && save.userId === current.id && save.pending.length > 0;
  const answered = get(state, ['richTextEditorAnswered']);
  const older = typeof requestedAt === 'number' && Boolean(answered) && answered.userId === current.id
    && requestedAt < answered.at;
  if (!out && !older) return user;
  return { ...user, advancedData: { ...user.advancedData, richTextEditor: flags } };
};

// ------------------------------------
// Reducer
// userUpdating
// ------------------------------------
export default function userReducer (state: ContextState, action: Action): ContextState {
  switch (action.type) {
    case USER_LOADING: return state
      .setIn(['user', 'userLoading'], true)
      .set('userLoading', true);

    case USER_LOADING_FAILED: return state
      .setIn(['user', 'userLoading'], false)
      .set('userLoading', false);

    case USER_LOADED:
      const user = {
        ...withSwitchKept(state, action.user, action.requestedAt),
        lastFetch: moment().toISOString(),
      };

      return state
        .set('userLoading', false)
        .set('user', user)
        .setIn(['user', 'userLoading'], false)
        .setIn(['doctors', 'savingWorkHours'], false)
        .setIn(['doctors', 'futureWorkHours'], {})
        .setIn(['doctors', 'deletingWorkHours'], {})
        .set('userUpdating', false);

    case USER_UPDATING:
      return state.set('userUpdating', true);

    case USER_UPDATE_SUCCESS:
      return state
        .set('userUpdating', false)
        .set('user', withSwitchKept(state, {
          ...state.user,
          ...action.user,
        }, action.requestedAt));

    case USER_UPDATE_ERROR:
      return state.set('userUpdating', false);

    case USER_RICH_TEXT_EDITOR_SAVING: {
      const current = get(state, ['user']);
      if (!current || current.id !== action.userId) return state;
      // Nothing out for this user: what the field shows now is what the server has
      const save = get(state, ['richTextEditorSave']);
      const waiting = save && save.userId === action.userId && save.pending.length ? save : {
        userId: action.userId,
        pending: [],
        confirmed: get(current, ['advancedData', 'richTextEditor', 'enabled']) === true,
        confirmedRequest: 0,
      };
      return withRichTextEditorFlags(state.set('richTextEditorSave', {
        ...waiting, pending: [...waiting.pending, action.request], latest: action.request,
      }), { enabled: action.enabled });
    }

    case USER_RICH_TEXT_EDITOR_SAVED: {
      if (!richTextEditorAnswerCounts(state, action.userId, action.request)) return state;
      const save = get(state, ['richTextEditorSave']);
      // The server's answer to a later switch than the one it last confirmed: what it has now
      const newer = !action.failed && action.request > save.confirmedRequest;
      const next = {
        ...save,
        pending: save.pending.filter((request) => request !== action.request),
        confirmed: newer ? action.saved.enabled === true : save.confirmed,
        confirmedRequest: newer ? action.request : save.confirmedRequest,
        resync: Boolean(save.resync || action.unanswered),
      };
      // Nothing out any more: nothing kept (the next switch starts from what the field shows then)
      const saved = state
        .set('richTextEditorSave', next.pending.length ? next : null)
        .set('richTextEditorAnswered', { userId: action.userId, at: action.answeredAt });
      // The latest switch still out: the field keeps showing it. Otherwise what the server last confirmed (and, with
      // the latest switch's own answer, the rest of what it says)
      if (next.pending.includes(save.latest)) return saved;
      const answer = action.request === save.latest && !action.failed ? action.saved : {};
      return withRichTextEditorFlags(saved, { ...answer, enabled: next.confirmed });
    }

    case USER_DELETING:
      return state.set('userDeleting', true);

    case USER_DELETE_SUCCESS:
      return state
        .set('userDeleting', false);

    case USER_DELETE_ERROR:
      return state.set('userDeleting', false);

    case USER_SET_LOADING: return state.set('userUpdating', action.isLoading);

    case USER_UPDATE_DOCTOR_PRIVACY_LOADING:
      return state
        .setIn(['user', 'isUpdatingPrivacy'], true);

    case USER_UPDATE_DOCTOR_PRIVACY_LOADED:
      return state
        .setIn(['user', 'advancedData', 'doctorDefaultVisibilityLevel'], action.doctorDefaultVisibilityLevel)
        .setIn(['user', 'isUpdatingPrivacy'], false);

    case USER_UPDATE_DOCTOR_PRIVACY_FAILED:
      return state
        .setIn(['user', 'isUpdatingPrivacy'], false);


      // create work hour
    case USER_CREATE_DOCTOR_WORKHOURS_LOADING:
      return state
        .setIn(['doctors', 'futureWorkHours', String(action.doctorId), String(action.future.id)], action.future)
        .setIn(['doctors', 'savingWorkHours', String(action.doctorId)], true);

    case USER_CREATE_DOCTOR_WORKHOURS_LOADED:
      const theDoctor = get(state, ['doctors', 'data', String(action.doctorId)], {});
      return state
        .setIn(['doctors', 'data', String(action.doctorId), 'workHours'], [action.workHours, ...get(theDoctor, 'workHours')])
        .setIn(['doctors', 'futureWorkHours', String(action.doctorId), String(action.futureId)], null)
        .setIn(['doctors', 'savingWorkHours', String(action.doctorId), String(action.futureId)], false);

    case USER_CREATE_DOCTOR_WORKHOURS_FAILED:
      return state
        .setIn(['doctors', 'futureWorkHours', String(action.doctorId), String(action.futureId)], null)
        .setIn(['doctors', 'savingWorkHours', String(action.doctorId), String(action.futureId)], false);


      // delete work hour


    case USER_DELETE_DOCTOR_WORKHOURS_LOADING:
      const delid = get(action, 'deleteId', null);
      return state
        .setIn(['doctors', 'deletingWorkHours', String(action.doctorId), delid], true);


    case USER_DELETE_DOCTOR_WORKHOURS_LOADED:
      const deleid = get(action, 'deleteId', null);
      const preWorkHours = get(state, ['doctors', 'data', String(action.doctorId), 'workHours']);
      return state
        .setIn(['doctors', 'data', String(action.doctorId), 'workHours'], filter(preWorkHours, item => String(item.id) !== String(deleid)))
        .setIn(['doctors', 'deletingWorkHours', String(action.doctorId), String(deleid)], false);


    case USER_DELETE_DOCTOR_WORKHOURS_FAILED:
      const deletid = get(action, 'deleteId', null);
      return state
        .setIn(['doctors', 'deletingWorkHours', String(action.doctorId), String(deletid)], false);


    case USER_INITIALIZE_WORKHOURS_LOAD_STATE:
      return state
        .setIn(['doctors', 'savingWorkHours'], false)
        .setIn(['doctors', 'futureWorkHours'], {})
        .setIn(['doctors', 'deletingWorkHours'], {});

      // password

    case USER_RECOVER_PASSWORD_LOADING:
      return state
        .setIn(['user', 'isRecoveringPassword'], true)
        .setIn(['user', 'recoverSuccess'], false)
        .setIn(['user', 'recoverPasswordError'], false);


    case USER_RECOVER_PASSWORD_LOADED:
      return state
        .setIn(['user', 'isRecoveringPassword'], false)
        .setIn(['user', 'recoverSuccess'], true)
        .setIn(['user', 'recoverPasswordError'], false);


    case USER_RECOVER_PASSWORD_FAILED:
      return state
        .setIn(['user', 'isRecoveringPassword'], false)
        .setIn(['user', 'recoverSuccess'], false)
        .setIn(['user', 'recoverPasswordError'], true);

    case USER_RECOVER_PASSWORD_RESET:
      return state
        .setIn(['user', 'isRecoveringPassword'], false)
        .setIn(['user', 'recoverSuccess'], false)
        .setIn(['user', 'recoverPasswordError'], false);


    case USER_UPDATE_PASSWORD_LOADING:
      return state
        .setIn(['user', 'isSavingPassword'], true)
        .setIn(['user', 'changePasswordError'], false)
        .setIn(['user', 'savingPasswordSuccess'], false);


    case USER_UPDATE_PASSWORD_LOADED:
      return state
        .setIn(['user', 'isSavingPassword'], false)
        .setIn(['user', 'changePasswordError'], false)
        .setIn(['user', 'savingPasswordSuccess'], true);

    // changePasswordError is a wrong current password, shown under its field. Any other failure (no answer, the
    // server's) has the toast, and isn't the user's fault.
    case USER_UPDATE_PASSWORD_FAILED:
      return state
        .setIn(['user', 'isSavingPassword'], false)
        .setIn(['user', 'changePasswordError'], Boolean(action.wrongPassword))
        .setIn(['user', 'savingPasswordSuccess'], false);

    case USER_UPDATE_PASSWORD_RESET:
      return state
        .setIn(['user', 'isSavingPassword'], false)
        .setIn(['user', 'changePasswordError'], false)
        .setIn(['user', 'savingPasswordSuccess'], false);


    case USER_REQUEST_BACKUP_LOADING:
      return state
        .setIn(['user', 'isRequestingBackup'], true)
        .setIn(['user', 'backupRequestsuccess'], false)
        .setIn(['subscription', 'errors'], null);

    case USER_REQUEST_BACKUP_LOADED:
      return state
        .setIn(['user', 'backupRequestsuccess'], true)
        .setIn(['user', 'isRequestingBackup'], false);

    case USER_REQUEST_BACKUP_FAILED:
      return state
        .setIn(['user', 'isRequestingBackup'], false)
        .setIn(['user', 'backupRequestsuccess'], false)
        .setIn(['user', 'errors'], [action.error.message]);

    case USER_REQUEST_BACKUP_RESET:
      return state
        .setIn(['user', 'backupRequestsuccess'], false)
        .setIn(['user', 'isRequestingBackup'], false);


    case USER_CREATE_CLINIC_LOADING:
      return state
        .setIn(['user', 'isCreatingClinic'], true);

    case USER_CREATE_CLINIC_LOADED:
      const normalizedNew = ClinicService.normalizeActiveClinic(get(action, 'activeClinic', {}));

      const userData = {
        ...action.user,
        lastFetch: moment().toISOString(),
      };

      return state
        .set('userLoading', false)
        .setIn(['doctors', 'savingWorkHours'], false)
        .setIn(['doctors', 'futureWorkHours'], {})
        .setIn(['doctors', 'deletingWorkHours'], {})
        .set('userUpdating', false)
        .set('user', userData)
        .setIn(['user', 'isCreatingClinic'], false)
        .setIn(['clinics', 'isLoading'], false)
        .setIn(['secretaries', 'isLoading'], false)
        .setIn(['secretaries', 'isSaving'], false)
        .setIn(['clinics', 'errors'], null)
        .setIn(['clinics', 'activeClinic'], normalizedNew.activeClinic)
        .setIn(['clinics', 'data'], null)// ???
        .setIn(['clinics', 'lastFetch'], moment().toISOString())
        .setIn(['doctors', 'isLoading'], false)
        .setIn(['doctors', 'data'], normalizedNew.doctors)
        .setIn(['appointments'], {})
        .setIn(['secretaries', 'data'], normalizedNew.secretaries)
        .setIn(['secretaries', 'managerIds'], normalizedNew.secretaryManagerIds)
        .setIn(['secretaries', 'nonManagerIds'], normalizedNew.secretaryNoManagerIds)
        .setIn(['doctors', 'lastFetch'], moment().toISOString()); // TODO: should be moved into DoctorsReducer

    case USER_CREATE_CLINIC_FAILED:
      return state
        .setIn(['user', 'isCreatingClinic'], false)
        .setIn(['user', 'errors'], [action.error.message]);
    default:
      return state;
  }
}
